import { Box, Button, Text, HStack, VStack, Tabs, TabList, Tab, TabPanels, TabPanel, Spinner, Checkbox, Input } from "@chakra-ui/react";

import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useState, useRef } from "react";
import { postSourcesUpdate } from "api/ai/ai.service";
import { getFileType, getFolderForRound, KB_FOLDER } from "api/common/fileinfo";
import fileinfoService, { apiAddToFolders, apiRestoreFromArchiveFileInfo, apiUpload } from "api/common/fileinfo.service";
import { useRightSidebar } from "context/RightSidebarContext";
import { UploadSimple, FilePdf, FileDoc, FileXls, FileCsv, File, X, WarningDiamond } from "@phosphor-icons/react";
import FileDragDrop from "components/FileDragDrop";
import FileDownloadButton from "components/FileDownloadButton";
import useAirUserRoles from "utils/AuthUtils";
import { useAuth } from "context";

const SOURCE_FILE_TYPE_ICONS = {
  PDF: FilePdf, DOCX: FileDoc, DOC: FileDoc, XLSX: FileXls, XLS: FileXls, CSV: FileCsv,
};

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_READABLE = `${MAX_FILE_SIZE_MB}MB`;

/**
 * File upload and management panel with tabs: Upload, Library, Archived
 */
export default function SourcesUploadRSidebar({ activeFiles, hideAIReviewButton, tables }) {
  const queryClient = useQueryClient();
  const roundSelector = useSelector((state) => state.issbRound);
  const { doClose } = useRightSidebar();
  const { isController } = useAirUserRoles();
  const { getAccessTokenSilently } = useAuth();

  const [fileInfoList, setFileInfoList] = useState([]);
  const [fileInfoLoading, setFileInfoLoading] = useState([]);
  const [librarySelected, setLibrarySelected] = useState([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [archivedSelected, setArchivedSelected] = useState([]);
  const [archivedSearch, setArchivedSearch] = useState("");
  const fileInputRef = useRef(null);

  let folder = getFolderForRound(roundSelector?.selectedRound?.id);
  if (tables?.includes(KB_FOLDER) && tables.length === 1) {
    folder = KB_FOLDER;
  }

  // Fetch all files for the Library tab (files not in the current round)
  const { data: allFilesPage } = useQuery(
    ["fileinfoService.list", "all"],
    async () => {
      await getAccessTokenSilently();
      return fileinfoService.list();
    },
    { enabled: isController() }
  );
  const libraryFiles = allFilesPage?.content?.filter((f) => !f.folders?.includes(folder) && !f.archived) || [];

  // Fetch archived files for the Archived tab
  const { data: archivedFilesPage } = useQuery(
    ["fileinfoService.list", "archived"],
    async () => {
      await getAccessTokenSilently();
      return fileinfoService.list({ query: "archived:true" });
    },
    { enabled: isController() }
  );
  const archivedFiles = archivedFilesPage?.content || [];

  const toggleLibrarySelect = (id) => {
    setLibrarySelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleAddLibraryToActive = () => {
    librarySelected.forEach((id) => {
      const fileInfo = libraryFiles.find((f) => f.id === id);
      if (fileInfo) apiAddToFolders(fileInfo, folder);
      postSourcesUpdate([id], true);
    });
    queryClient.invalidateQueries("fileinfoService.list");
    setLibrarySelected([]);
    doClose();
  };

  const toggleArchivedSelect = (id) => {
    setArchivedSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleRestoreArchived = () => {
    archivedSelected.forEach((id) => {
      const fileInfo = archivedFiles.find((f) => f.id === id);
      if (fileInfo) {
        apiRestoreFromArchiveFileInfo(fileInfo);
        postSourcesUpdate([id], true);
      }
    });
    queryClient.invalidateQueries("fileinfoService.list");
    queryClient.invalidateQueries(["fileinfoService.list", "archived"]);
    setArchivedSelected([]);
    doClose();
  };

  const onFileInfoListChange = (newFileInfoList) => {
    queryClient.invalidateQueries("fileinfoService.list");
    const newFiles = newFileInfoList.filter((f) => !fileInfoList.includes(f));
    setFileInfoList(newFileInfoList);
    newFiles.forEach((f) => apiAddToFolders(f.id, folder));
    newFiles.forEach((f) => postSourcesUpdate([f.id], true));
  };

  const handleFileUpload = (files) => {
    if (!files || !files[0]) return;

    const fileArray = Array.from(files);
    const validFiles = [];
    const oversizedFiles = [];

    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    }

    if (oversizedFiles.length > 0) {
      setFileInfoLoading((prev) => [
        ...prev,
        ...oversizedFiles.map((file) => ({
          id: `error_${file.name}_${file.size}_${Date.now()}`,
          filename: file.name,
          size: file.size,
          type: file.type,
          loading: false,
          error: true,
          errorMessage: `Too large (max ${MAX_FILE_SIZE_READABLE})`,
        })),
      ]);
    }

    if (validFiles.length === 0) return;

    for (const newFile of validFiles) {
      setFileInfoLoading((prev) => [
        ...prev,
        { id: newFile.name, filename: newFile.name, size: newFile.size, type: newFile.type, loading: true },
      ]);
    }

    const fileInfoList_local = [...fileInfoList];
    (async () => {
      for (const newFile of validFiles) {
        const formData = new FormData();
        formData.append("file", newFile);
        if (folder) formData.append("folders", [folder]);
        formData.append("tags", ["ISSB"]);

        try {
          const newFileInfo = await apiUpload({ data: formData });
          setFileInfoLoading((prev) =>
            prev.filter((file) => !(file.filename === newFile.name && file.size === newFile.size && file.type === newFile.type))
          );
          queryClient.invalidateQueries("fileinfoService.list");
          fileInfoList_local.push(newFileInfo);
          onFileInfoListChange(fileInfoList_local);
        } catch (err) {
          console.error("Error uploading file", err);
          setFileInfoLoading((prev) =>
            prev.map((file) => {
              if (file.filename === newFile.name && file.size === newFile.size && file.type === newFile.type) {
                return { ...file, loading: false, error: true };
              }
              return file;
            })
          );
        }
      }
    })();
  };

  return (
    <Box id="SourcesUploadRSidebar" display="flex" flexDirection="column" h="full">
      <RightSidebarHeader>Add Active Document</RightSidebarHeader>

      <Tabs mt={4} variant="primary" display="flex" flexDirection="column" flex={1} minH={0}>
        <TabList borderBottom="1px solid" borderColor="gray.200" flexShrink={0}>
          <Tab fontSize="sm">Upload</Tab>
          {isController() && <Tab fontSize="sm">Library <Text as="span" fontWeight="normal" color="gray.500" ml={1}>({libraryFiles.length})</Text></Tab>}
          {isController() && <Tab fontSize="sm">Archived <Text as="span" fontWeight="normal" color="gray.500" ml={1}>({archivedFiles.length})</Text></Tab>}
        </TabList>

        <TabPanels
          flex={1}
          overflowY="auto"
          minH={0}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          {/* Upload Tab */}
          <TabPanel px={0} pt={4}>
            <Text color="gray.500" mb={4}>
              Upload a new document to make it available to the AI for this round.
            </Text>

            <FileDragDrop onFiles={handleFileUpload} borderRadius="lg">
              <VStack
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="lg"
                py={10}
                px={6}
                spacing={3}
                cursor="pointer"
                _hover={{ borderColor: "gray.400" }}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadSimple size={32} color="gray" />
                <Text fontWeight="semibold" textAlign="center">Drop files here or click to browse</Text>
                <Text fontSize="sm" color="gray.500">PDF, DOCX, XLSX up to {MAX_FILE_SIZE_READABLE}</Text>
              </VStack>
            </FileDragDrop>

            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                handleFileUpload(e.target.files);
                e.target.value = null;
              }}
              accept=".pdf,.docx,.xlsx,.xls,.doc,.csv"
            />

            {(fileInfoList.length > 0 || fileInfoLoading.length > 0) && (
              <Box mt={4}>
                <Text fontSize="sm" fontWeight="semibold" mb={2} px={1}>Uploaded files</Text>
                <Box borderTop="1px solid" borderColor="gray.100">
                  <VStack spacing={0} align="stretch">
                    {fileInfoLoading.map((file) => {
                      const isError = !!file.error;
                      return (
                        <HStack key={file.id} py={1.5} px={1} spacing={2} borderBottom="1px solid" borderColor="gray.100">
                          {isError ? (
                            <WarningDiamond size={22} color="#E53E3E" />
                          ) : (
                            <Spinner size="sm" />
                          )}
                          <Text fontSize="body.xs" color={isError ? "red.500" : "gray.600"} flex={1} noOfLines={1}>
                            {file.filename}{isError && ` — ${file.errorMessage || "Upload failed"}`}
                          </Text>
                        </HStack>
                      );
                    })}
                    {fileInfoList.map((fileinfo) => {
                      const fileType = getFileType(fileinfo);
                      const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
                      return (
                        <HStack key={fileinfo.id} py={1.5} px={1} spacing={2} borderBottom="1px solid" borderColor="gray.100">
                          <Box flexShrink={0}>
                            <IconComponent size={22} color="#718096" />
                          </Box>
                          <FileDownloadButton fileInfo={fileinfo} as={Text} fontSize="body.xs" color="darkTeal" flex={1} noOfLines={1}>
                            {fileinfo.title || fileinfo.filename || fileinfo.id}
                          </FileDownloadButton>
                          <Box
                            as="button"
                            flexShrink={0}
                            onClick={() => setFileInfoList((prev) => prev.filter((f) => f.id !== fileinfo.id))}
                            title="Remove"
                            _hover={{ color: "red.500" }}
                            color="gray.400"
                          >
                            <X size={16} />
                          </Box>
                        </HStack>
                      );
                    })}
                  </VStack>
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* Library Tab */}
          {isController() && (
            <TabPanel px={0} pt={4}>
              <Text color="gray.500" mb={4}>
                Select library documents to add to the active documents for this round.
              </Text>

              {libraryFiles.length === 0 ? (
                <Text color="gray.400" fontSize="sm">No library documents available to add.</Text>
              ) : (
                <>
                  <Input
                    placeholder="Search documents..."
                    size="sm"
                    borderRadius="full"
                    borderColor="gray.300"
                    mb={3}
                    w="80%"
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    type="search"
                  />
                  <Box borderTop="1px solid" borderColor="gray.100">
                    <VStack spacing={0} align="stretch">
                      {libraryFiles.filter((f) => !librarySearch || (f.title || f.filename || "").toLowerCase().includes(librarySearch.toLowerCase())).map((fileinfo) => {
                        const fileType = getFileType(fileinfo);
                        const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
                        const isSelected = librarySelected.includes(fileinfo.id);
                        return (
                          <HStack
                            key={fileinfo.id}
                            py={1.5}
                            px={1}
                            spacing={2}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            cursor="pointer"
                            bg={isSelected ? "green.50" : "transparent"}
                            _hover={{ bg: isSelected ? "green.50" : "gray.50" }}
                            onClick={() => toggleLibrarySelect(fileinfo.id)}
                          >
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => toggleLibrarySelect(fileinfo.id)}
                              colorScheme="green"
                              size="sm"
                            />
                            <Box flexShrink={0}>
                              <IconComponent size={22} color="#718096" />
                            </Box>
                            <Text fontSize="body.xs" color="darkTeal" flex={1} noOfLines={1}>
                              {fileinfo.title || fileinfo.filename || fileinfo.id}
                            </Text>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>

                </>
              )}
            </TabPanel>
          )}

          {/* Archived Tab */}
          {isController() && (
            <TabPanel px={0} pt={4}>
              <Text color="gray.500" mb={4}>
                Select archived documents to restore and add to the active documents for this round.
              </Text>

              {archivedFiles.length === 0 ? (
                <Text color="gray.400" fontSize="sm">No archived documents available.</Text>
              ) : (
                <>
                  <Input
                    placeholder="Search documents..."
                    size="sm"
                    borderRadius="full"
                    borderColor="gray.300"
                    mb={3}
                    w="80%"
                    value={archivedSearch}
                    onChange={(e) => setArchivedSearch(e.target.value)}
                    type="search"
                  />
                  <Box borderTop="1px solid" borderColor="gray.100">
                    <VStack spacing={0} align="stretch">
                      {archivedFiles.filter((f) => !archivedSearch || (f.title || f.filename || "").toLowerCase().includes(archivedSearch.toLowerCase())).map((fileinfo) => {
                        const fileType = getFileType(fileinfo);
                        const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
                        const isSelected = archivedSelected.includes(fileinfo.id);
                        return (
                          <HStack
                            key={fileinfo.id}
                            py={1.5}
                            px={1}
                            spacing={2}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            cursor="pointer"
                            bg={isSelected ? "green.50" : "transparent"}
                            _hover={{ bg: isSelected ? "green.50" : "gray.50" }}
                            onClick={() => toggleArchivedSelect(fileinfo.id)}
                          >
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => toggleArchivedSelect(fileinfo.id)}
                              colorScheme="green"
                              size="sm"
                            />
                            <Box flexShrink={0}>
                              <IconComponent size={22} color="#718096" />
                            </Box>
                            <Text fontSize="body.xs" color="darkTeal" flex={1} noOfLines={1}>
                              {fileinfo.title || fileinfo.filename || fileinfo.id}
                            </Text>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>
                </>
              )}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      <Box pt={4} flexShrink={0} borderTop="1px solid" borderColor="gray.100">
        {librarySelected.length > 0 && (
          <Button variant="primary" w="full" mb={2} onClick={handleAddLibraryToActive}>
            Add {librarySelected.length} to Active
          </Button>
        )}
        {archivedSelected.length > 0 && (
          <Button variant="primary" w="full" mb={2} onClick={handleRestoreArchived}>
            Restore {archivedSelected.length} to Active
          </Button>
        )}
        <Button variant="secondary" w="full" onClick={doClose}>Close</Button>
      </Box>
    </Box>
  );
}
